import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { matchPath, useNavigate } from "react-router-dom";
import SignInSheet from "@/app/components/auth/SignInSheet";
import { Modal } from "@/app/components/ui/modal.jsx";
import { buildDocumentUrl } from "@/app/handlers/documentStorage";
import { stripHTML } from "@/app/handlers/validationFunctions.js";
import { useFeatureFlag } from "@/app/hooks/useFeatureFlag.jsx";
import type { GoogleSession } from "@/app/services/auth/googleSignIn";
import { fetchSmartProfile, smartProfileKey } from "@/app/services/create-resume/queries";
import { useAuth } from "../../../../../context/Auth/AuthContext";
import DSPModal from "../../../../JobsPage/components/jobApplication/DSPModal";
import GenerateSingleJobImage from "../../../../Social/GenerateSingleJobImage";
import ApplyModal from "../ApplyModal";
import Loader from "../Loader";
// Components
import { ApplicationProcess } from "./components/ApplicationProcess";
import { CompanyCard } from "./components/CompanyCard";
import { FitCheckSection } from "./components/FitCheckSection";
import { JobActionButtons } from "./components/JobActionButtons";
import { JobDescription } from "./components/JobDescription";
import { JobHeader } from "./components/JobHeader";
import { JobMetadata } from "./components/JobMetadata";
import { MoreRolesFromCompany } from "./components/MoreRolesFromCompany";
import { RoleExplainerSection } from "./components/RoleExplainerSection";
import { SimilarRoles } from "./components/SimilarRoles";
// Hooks
import { useFitCheckFlow } from "./hooks/useFitCheckFlow";
import { useJobApplication } from "./hooks/useJobApplication";
import { useJobDetails } from "./hooks/useJobDetails";
import { useJobInteractions } from "./hooks/useJobInteractions";
import { RoleExplainerSurface, useJobPageAnalytics } from "./hooks/useJobPageAnalytics";
import { useRoleExplainer } from "./hooks/useRoleExplainer";
import { useRoleExplainerViewState } from "./hooks/useRoleExplainerViewState";
// Utils
import { isJobExpired, JobDetails, toLowerCaseCapitalize } from "./utils/helpers";

interface ViewJobProps {
	data?: { _id?: string };
	setData?: (data: JobDetails) => void;
	setShowJobDetailsModal?: (show: boolean) => void;
	/**
	 * Which surface this instance renders on, reported with Role Explainer events.
	 * Defaults from the `data` prop: the standalone page reads the job from the URL,
	 * every drawer host passes the job in.
	 */
	surface?: RoleExplainerSurface;
}

function ViewJob({ data, setData, setShowJobDetailsModal, surface }: ViewJobProps) {
	const baseUrl = `${window.location.origin}/`;
	const navigate = useNavigate();
	const { isLoggedIn: isUserLoggedIn } = useAuth();
	const isOnJobPage = window.location.pathname.includes("/jobs/");

	// embedPage is true when we are on /companies/:id/embed page
	const embedPage = matchPath("/companies/:id/embed", window.location.pathname);

	const effectiveSurface: RoleExplainerSurface = surface ?? (data ? "job_drawer" : "job_details_page");

	// Custom hooks
	const { jobId, jobDetails, loading, applicationDetails, setApplicationDetails } = useJobDetails(data);
	const { saveJobInteraction } = useJobInteractions(jobId, data?._id);
	const analytics = useJobPageAnalytics(jobDetails, effectiveSurface);
	const { roleExplanations, explainerIsLoading, explainerHasError, handleRoleExplainer } = useRoleExplainer(
		jobDetails,
		{
			onLoaded: analytics.trackRoleExplainerViewed,
			onFailed: analytics.trackRoleExplainerFailed,
		},
	);
	const { isOnProfilePage: _isOnProfilePage, isInBasket: _isInBasket } = useFitCheckFlow(
		jobDetails,
		setShowJobDetailsModal,
	);
	const {
		showDSPModal,
		setShowDSPModal,
		showApplyModal,
		setShowApplyModal,
		scrollToEmail,
		applicationRef,
		onApply,
		handleDSPModalSelect,
		dspModalData,
	} = useJobApplication(applicationDetails, setApplicationDetails, jobId);

	// Role Explainer view state (localStorage)
	const { hasViewedRoleExplainer, markRoleExplainerViewed } = useRoleExplainerViewState(jobId);

	// Auto-load role explainer if user has previously viewed it.
	// Guarded per job: without the ref a failed run leaves roleExplanations null with
	// loading back to false, which re-triggers this effect indefinitely.
	// Held until the job itself has arrived, because the explainer posts the job body upstream.
	const { trackRoleExplainerAutoLoaded } = analytics;
	const autoLoadedJobRef = useRef<string | null>(null);
	useEffect(() => {
		if (!jobId || !jobDetails?._id || !hasViewedRoleExplainer) return;
		if (autoLoadedJobRef.current === jobId) return;
		if (roleExplanations || explainerIsLoading || explainerHasError) return;

		autoLoadedJobRef.current = jobId;
		trackRoleExplainerAutoLoaded();
		handleRoleExplainer("auto_reload");
	}, [
		jobId,
		jobDetails?._id,
		hasViewedRoleExplainer,
		roleExplanations,
		explainerIsLoading,
		explainerHasError,
		handleRoleExplainer,
		trackRoleExplainerAutoLoaded,
	]);

	// Mark role explainer as viewed when it loads successfully
	useEffect(() => {
		if (roleExplanations && !hasViewedRoleExplainer) {
			markRoleExplainerViewed();
		}
	}, [roleExplanations, hasViewedRoleExplainer, markRoleExplainerViewed]);

	// Feature flags
	const showPostGenerator = useFeatureFlag("posterGenerator");

	// Post generator modal
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleMenuClick = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);

	// Page-level language toggle for role guidance (Role Explainer + FitCheck)
	const [guidanceLanguage, setGuidanceLanguage] = useState<"en" | "sw">("en");

	// Whether the explanation is open in its accordion. The job description hides behind a
	// summary while it is, so this state is what lets the posting come back when it is folded.
	const [explainerExpanded, setExplainerExpanded] = useState(true);

	const queryClient = useQueryClient();
	const [showSignInSheet, setShowSignInSheet] = useState(false);
	const [fitCheckStartSignal, setFitCheckStartSignal] = useState(0);

	const handleFitCheckClick = () => {
		analytics.trackFitCheckStarted();
		if (isUserLoggedIn) return true;

		setShowSignInSheet(true);
		return false;
	};

	// After sign-in the inline FitCheck runs the assessment itself, so we only need to open it.
	const runFitCheck = async (_resumeURL: string): Promise<void> => {
		setShowSignInSheet(false);
		setFitCheckStartSignal((signal) => signal + 1);
	};

	const handleSignedInForFitCheck = async (_session: GoogleSession, { hasAccount }: { hasAccount: boolean }) => {
		posthog.capture("fitcheck_signed_in_from_job", { jobId: jobDetails?._id, hasAccount });

		if (!hasAccount) return null;

		const smartProfile = await queryClient
			.fetchQuery({ queryKey: smartProfileKey, queryFn: fetchSmartProfile })
			.catch(() => null);

		const dsps: Array<{ source_resume?: { storage_key?: string } }> = smartProfile?.dsps ?? [];
		return buildDocumentUrl(dsps.find((dsp) => dsp.source_resume?.storage_key)?.source_resume?.storage_key);
	};

	// Navigation
	const handleBackButton = () => {
		if (isOnJobPage) {
			navigate("/jobs");
		} else if (setShowJobDetailsModal) {
			setShowJobDetailsModal(false);
		}
	};

	return (
		<>
			{loading && (
				<div className="flex items-center justify-center min-h-screen">
					<Loader />
				</div>
			)}
			{!loading && (
				<>
					<div className="min-h-screen">
						<Helmet>
							<title>
								{jobDetails?.jobTitle} job at {jobDetails?.company?.company_name || ""} in{" "}
								{jobDetails?.jobLocation?.region}, Tanzania
							</title>
							<meta name="description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta
								name="keywords"
								content={`${jobDetails?.jobTitle} job, ${jobDetails?.company?.company_name || ""}, ${jobDetails?.jobLocation?.region} Tanzania`}
							/>
							<meta property="og:title" content={jobDetails?.jobTitle} />
							<meta property="og:description" content={stripHTML(jobDetails?.jobDescription?.lookingToHire)} />
							<meta property="og:image" content={jobDetails?.company?.logo_url} />
							<meta property="og:url" content={window.location.href} />
						</Helmet>

						<div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-8">
							<JobHeader
								jobDetails={jobDetails}
								baseUrl={baseUrl}
								onBack={handleBackButton}
								onShare={() => saveJobInteraction("Shared")}
							/>

							<div className="mb-8">
								<div>
									<JobMetadata
										jobDetails={jobDetails}
										showPostGenerator={showPostGenerator}
										onMenuClick={handleMenuClick}
										onSave={() => {
											saveJobInteraction("Saved");
											analytics.trackJobSaved();
										}}
										onShare={() => {
											saveJobInteraction("Shared");
											analytics.trackJobShared();
										}}
										onNotRelevant={() => {
											saveJobInteraction("Irrevant");
											analytics.trackJobMarkedIrrelevant();
										}}
										isExpired={isJobExpired(jobDetails.closeAt)}
									/>

									{/* CTA Section - Role Explainer */}
									{!isJobExpired(jobDetails.closeAt) && (
										<JobActionButtons
											explainerIsLoading={explainerIsLoading}
											roleExplanations={roleExplanations}
											onRoleExplainer={() => {
												handleRoleExplainer("click");
												analytics.trackRoleExplainerOpened("click");
											}}
										/>
									)}
								</div>

								{/* Main content */}
								<div className="pt-3">
									{/* 1. Role Explainer Section - show when loading, has content, or failed */}
									{(explainerIsLoading || roleExplanations || explainerHasError) && (
										<RoleExplainerSection
											roleExplanations={roleExplanations}
											isLoading={explainerIsLoading}
											hasError={explainerHasError}
											onExplain={() => {
												handleRoleExplainer("retry");
												analytics.trackRoleExplainerOpened("retry");
											}}
											lang={guidanceLanguage}
											onLangChange={setGuidanceLanguage}
											onExpandedChange={setExplainerExpanded}
										/>
									)}

									{/* 2. Job Description - collapsed only while the explanation is open on screen,
									    so folding the explainer away brings the original posting back into view. */}
									<JobDescription
										jobDetails={jobDetails}
										isCollapsedByDefault={hasViewedRoleExplainer && roleExplanations !== null && explainerExpanded}
									/>

									{/* 3. FitCheck Section - only show if role explainer has been viewed.
									    Sits between the posting and the apply steps: read the role, then check
									    your fit, then apply. */}
									{!embedPage && (
										<FitCheckSection
											jobDetails={jobDetails}
											onFitCheckClick={handleFitCheckClick}
											startSignal={fitCheckStartSignal}
										/>
									)}

									<div className="">
										{/* 4. Commitment Zone - Apply section */}
										<ApplicationProcess
											jobDetails={jobDetails}
											applicationDetails={applicationDetails}
											scrollToEmail={scrollToEmail}
											applicationRef={applicationRef}
											onApply={onApply}
											onSave={() => saveJobInteraction("Saved")}
											hasViewedExplainer={roleExplanations !== null}
											hasCompletedFitCheck={false}
										/>
									</div>

									{/* 5. Other live roles at the same employer. Standalone page only: the drawer
									    is opened from a list that already shows them, and it has nowhere to
									    navigate to without closing itself. */}
									{effectiveSurface === "job_details_page" && !embedPage && (
										<MoreRolesFromCompany
											companyId={jobDetails?.company?._id}
											companyName={jobDetails?.company?.company_name}
											companyLogo={jobDetails?.company?.logo_url}
											currentJobId={jobDetails?._id}
										/>
									)}
								</div>
							</div>

							{/* Company Card */}
							{!embedPage && <CompanyCard jobDetails={jobDetails} />}

							{/* Similar roles. Widens the circle after the employer's own roles and profile:
							    same category, other employers. Replaces the bare "explore more roles" link
							    that used to close the page — cards here, rows in the narrower drawer. */}
							{!embedPage && <SimilarRoles jobDetails={jobDetails} surface={effectiveSurface} />}

							{/* Apply Modal */}
							<ApplyModal
								isOpen={showApplyModal}
								onClose={() => setShowApplyModal(false)}
								jobTitle={jobDetails.jobTitle}
								companyName={(jobDetails as { companyName?: string }).companyName}
								applicationDetails={applicationDetails}
								onDrawer={true}
							/>

							<DSPModal
								data={dspModalData}
								isOpen={showDSPModal}
								onSubmit={handleDSPModalSelect}
								onClose={() => setShowDSPModal(false)}
								action={(dspModalData as { action?: string })?.action}
							/>

							<SignInSheet
								open={showSignInSheet}
								onOpenChange={setShowSignInSheet}
								title="Sign in to check your fit"
								description={`We compare your resume to ${jobDetails?.jobTitle || "this role"} and show you what is missing.`}
								resumeTitle="Add your resume"
								resumeDescription="Upload a PDF and we'll check it against this role straight away."
								onSignedIn={handleSignedInForFitCheck}
								onResumeReady={runFitCheck}
								busyLabel="Checking your fit..."
							/>
						</div>
					</div>

					{/* Post Generator Modal */}
					<Modal
						isOpen={isModalOpen}
						onClose={handleCloseModal}
						title="Generate Image"
						description=""
						className=""
						titleClassName=""
					>
						<GenerateSingleJobImage
							companyName={jobDetails?.company?.company_name || ""}
							jobTitle={jobDetails?.jobTitle}
							jobDescription={stripHTML(jobDetails?.jobDescription?.lookingToHire).split(".")[0]}
							qualification={
								toLowerCaseCapitalize(jobDetails?.educationQualification?.minEducationLevel) +
									" in " +
									(jobDetails?.educationQualification?.relevantMajors?.join(", ") || "") || ""
							}
							deadline={dayjs(jobDetails?.closeAt).format("MMM DD, YYYY")}
							jobLocation={`${jobDetails?.jobLocation?.region}`}
							jobLink={`www.dproz.com/companies/${jobDetails?.company?._id}`}
						/>
					</Modal>
				</>
			)}
		</>
	);
}

export default ViewJob;
